'use client';

import React, { useEffect, useState } from 'react';
import { History, Loader2, RotateCcw, X } from 'lucide-react';
import { api } from '@/lib/api';

export interface SiteRevisionRow {
  id: number;
  name: string;
  publishedAt: string | null;
  createdAt: string | null;
  sectionCount?: number;
}

interface RevisionHistoryPanelProps {
  open: boolean;
  listUrl: string;
  restoreUrl: (id: number) => string;
  onClose: () => void;
  onRestored: (draft: unknown) => void;
}

export function RevisionHistoryPanel({
  open,
  listUrl,
  restoreUrl,
  onClose,
  onRestored,
}: RevisionHistoryPanelProps) {
  const [rows, setRows] = useState<SiteRevisionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await api<{ revisions: SiteRevisionRow[] }>(listUrl);
        if (!cancelled) setRows(Array.isArray(data.revisions) ? data.revisions : []);
      } catch {
        if (!cancelled) setError('Could not load revision history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listUrl]);

  if (!open) return null;

  async function restore(id: number) {
    setRestoringId(id);
    setError(null);
    try {
      const data = await api<{ page?: unknown }>(restoreUrl(id), { method: 'POST' });
      onRestored(data.page ?? data);
      onClose();
    } catch {
      setError('Restore failed. The published site was not changed.');
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="revision-history-title">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted" />
            <div>
              <h2 id="revision-history-title" className="text-sm font-semibold text-ink">Revision history</h2>
              <p className="text-[11px] text-muted">Restore copies into the draft. Live site stays until you publish.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-shell" aria-label="Close revisions">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto p-4">
          {loading && (
            <p className="flex items-center gap-2 text-xs text-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </p>
          )}
          {error && <p className="text-xs font-semibold text-danger">{error}</p>}
          {!loading && rows.length === 0 && (
            <p className="rounded-xl border border-dashed border-line bg-shell/50 px-3 py-8 text-center text-xs text-muted">
              No published revisions yet. Publish this page to start history.
            </p>
          )}
          {rows.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-shell/40 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-ink">{row.name}</p>
                <p className="text-[11px] text-muted">
                  {row.publishedAt ? new Date(row.publishedAt).toLocaleString() : 'Unknown date'}
                  {typeof row.sectionCount === 'number'
                    ? ` · ${row.sectionCount} section${row.sectionCount === 1 ? '' : 's'}`
                    : null}
                </p>
              </div>
              <button
                type="button"
                disabled={restoringId === row.id}
                onClick={() => void restore(row.id)}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-line bg-card px-2 py-1.5 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
              >
                {restoringId === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Restore
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
